import React, {useEffect, useState} from 'react';
import {connect} from "react-redux";
import {object, string} from 'yup';
import {Inventory2Outlined} from "@mui/icons-material";
import {useNavigate, useParams} from "react-router-dom";
import {Formik} from "formik";
import {conteneursActions} from "../../modules/conteneurs/conteneurs.actions";
import {patrimoinesActions} from "../../modules/patrimoines/patrimoines.actions";
import ConteneurDataField from "./conteneur/form/ConteneurDataField";
import SubmitButton from "./layout/forms/SubmitButton";
import PageTitle from "./layout/PageTitle";
import Loader from "./layout/loading/loader";
import Grid from "@mui/material/Grid2";
import Constants from "../../utils/constants";

const conteneurValidationSchema = object({
    patrimoine: string().required("Le patrimoine est requis"),
    nomenclature: string().required("La nomenclature est requise"),
    adresse: string().required("L'adresse est requise"),
    commune: string().required("La commune est requise"),
    latitude: string().required("La latitude est requise"),
    longitude: string().required("La longitude est requise"),
    conteneurType: string().required("Le type de conteneur est requis"),
    dechetType: string().required("Le type de déchet est requis"),
    quantite: string().required("La quantité est requise"),
});

function ConteneurNewOrEdit({dispatch, ...props}) {
    const params = useParams();
    const navigate = useNavigate();
    const [patrimoineId, setPatrimoineId] = useState(null);
    const [selectedPatrimoineId, setSelectedPatrimoineId] = useState(null);
    const [conteneurId, setConteneurId] = useState(null);
    const [mode, setMode] = useState("create");

    useEffect(() => {
        if (params.hasOwnProperty("patrimoineId")) {
            setPatrimoineId(params.patrimoineId);
            setSelectedPatrimoineId(params.patrimoineId);
        }
        if (params.hasOwnProperty("conteneurId")) {
            setConteneurId(params.conteneurId);
            setMode("edit");
        }
        return () => {
            dispatch(conteneursActions.clearConteneurs());
            dispatch(patrimoinesActions.clearPatrimoine());
        };
    }, [params]);

    useEffect(() => {
        if (patrimoineId && (String(props.patrimoine?.id) !== String(patrimoineId)) && !props.loading && !props.error) {
            dispatch(patrimoinesActions.fetchPatrimoine(patrimoineId));
        }
    }, [patrimoineId, props.patrimoine, props.loading, props.error, dispatch]);

    useEffect(() => {
        if (!props.patrimoines && !props.loading && !props.error) {
            const clientId = window.localStorage.getItem('clientSelected');
            if (clientId) {
                dispatch(patrimoinesActions.fetchPatrimoinesLight(clientId));
            }
        }
    }, [props.patrimoines, props.loading, props.error, dispatch]);

    useEffect(() => {
        if (conteneurId && (String(props.conteneur?.id) !== String(conteneurId)) && !props.loading && !props.error) {
            dispatch(conteneursActions.fetchConteneur(conteneurId));
        }
    }, [conteneurId, props.conteneur, props.loading, props.error, dispatch]);

    useEffect(() => {
        if (props.conteneur?.patrimoine && mode === "edit" && !selectedPatrimoineId) {
            const id = props.conteneur.patrimoine.split('/').pop();
            setSelectedPatrimoineId(id);
        }
    }, [props.conteneur, mode, selectedPatrimoineId]);

    const initialValues = {
        patrimoine: selectedPatrimoineId ? `/api/patrimoines/${selectedPatrimoineId}` : '',
        nomenclature: props.conteneur?.nomenclature || '',
        adresse: props.conteneur?.adresse || '',
        complementAdresse: props.conteneur?.complementAdresse || '',
        commune: props.conteneur?.commune || '',
        latitude: props.conteneur?.latitude || '',
        longitude: props.conteneur?.longitude || '',
        conteneurType: props.conteneur?.conteneurType || 'AB',
        dechetType: props.conteneur?.dechetType || 'BIO',
        quantite: props.conteneur?.quantite || 1,
        marque: props.conteneur?.marque || '',
    };

    const handleSubmit = (values) => {
        const targetPatrimoineId = values.patrimoine ? values.patrimoine.split('/').pop() : patrimoineId;
        const detailRoute = Constants.APP_ROUTE_PATRIMOINE_DETAIL.replace(":id", targetPatrimoineId);
        if (mode === "create") {
            dispatch(conteneursActions.createConteneur(values, () => {
                navigate(detailRoute);
            }));
        } else {
            dispatch(conteneursActions.updateConteneur(conteneurId, values, () => {
                navigate(detailRoute);
            }));
        }
    };

    return (
        <>
            <PageTitle
                title={mode === "create"
                    ? `Nouveau conteneur pour ${props.patrimoine?.libelle || '...'}`
                    : `Modifier le conteneur ${props.conteneur?.nomenclature || props.conteneur?.adresse || '...'}`}
                icon={<Inventory2Outlined/>}
            />
            <Formik
                initialValues={initialValues}
                enableReinitialize={true}
                validationSchema={conteneurValidationSchema}
                onSubmit={(values, {setSubmitting}) => {
                    handleSubmit(values);
                }}
            >
                {({
                      values,
                      errors,
                      touched,
                      handleChange,
                      handleBlur,
                      handleSubmit,
                      isSubmitting,
                  }) => (
                    <form onSubmit={handleSubmit}>
                        {props.loading && <Loader/>}
                        {!props.loading && (
                            <>
                                <ConteneurDataField
                                    values={values}
                                    errors={errors}
                                    handleChange={handleChange}
                                    handleBlur={handleBlur}
                                    mode={mode}
                                    patrimoines={props.patrimoines}
                                />
                                <Grid container justifyContent="center" style={{marginTop: '2rem'}}>
                                    <Grid size={4}>
                                        <SubmitButton submitting={isSubmitting && !props.error}/>
                                    </Grid>
                                </Grid>
                            </>
                        )}
                    </form>
                )}
            </Formik>
        </>
    );
}

const mapStateToProps = function (state) {
    return {
        patrimoine: state.patrimoines.patrimoine,
        patrimoines: state.patrimoines.patrimoines,
        conteneur: state.conteneurs.conteneur,
        loading: state.patrimoines.loading || state.conteneurs.loading,
        error: state.patrimoines.error || state.conteneurs.error,
    };
};

export default connect(mapStateToProps)(ConteneurNewOrEdit);
